/**
 * EntityTabBar Component
 * Tab bar for switching between different content types within an expanded entity card
 */

import React from 'react';
import type { EntityTabBarProps, EntityTab } from './types';
import { TAB_LABELS } from './types';

// Responsive labels for RSAs/RDAs - full names on desktop, abbreviations on mobile
const getResponsiveLabel = (tab: EntityTab): React.ReactNode => {
  if (tab === 'rsas') {
    return (
      <>
        <span className="sm:hidden">RSAs</span>
        <span className="hidden sm:inline">Responsive Search Ads</span>
      </>
    );
  }
  if (tab === 'rdas') {
    return (
      <>
        <span className="sm:hidden">RDAs</span>
        <span className="hidden sm:inline">Responsive Display Ads</span>
      </>
    );
  }
  return TAB_LABELS[tab];
};

export function EntityTabBar({
  availableTabs,
  activeTab,
  onChange,
  counts,
}: EntityTabBarProps) {
  return (
    <div className="border-b border-gray-200 bg-gray-50">
      <nav className="flex -mb-px" aria-label="Entity tabs">
        {availableTabs.map((tab) => {
          const isActive = activeTab === tab;
          const count = counts[tab];
          const label = getResponsiveLabel(tab);

          return (
            <button
              key={tab}
              onClick={() => onChange(tab)}
              className={`
                px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                ${isActive
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab}`}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span
                  className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default EntityTabBar;
